import React, { useState, useEffect } from 'react';
import { X, Lightbulb, Eye, CheckCircle, XCircle, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const DIFF_COLORS = {
  easy:   { bg: '#dcfce7', color: '#15803d' },
  medium: { bg: '#fef3c7', color: '#92400e' },
  hard:   { bg: '#fee2e2', color: '#991b1b' },
};

const unescape = (s) => (s || '').replace(/\\n/g, '\n').replace(/\\t/g, '\t');

const Md = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
    {unescape(children)}
  </ReactMarkdown>
);

const PracticeProblemModal = ({
  problem,
  isOpen,
  onClose,
  onSubmit,
  translations = {},
  currentIndex = 0,
  totalProblems = 1,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}) => {
  const [userSolution, setUserSolution] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [startTime] = useState(Date.now);

  useEffect(() => {
    if (isOpen) {
      setUserSolution('');
      setShowHint(false);
      setShowAnswer(false);
      setIsCorrect(null);
      setSubmitted(false);
    }
  }, [isOpen, currentIndex]);

  if (!isOpen || !problem) return null;

  const diff = (problem.difficulty || 'medium').toLowerCase();
  const diffStyle = DIFF_COLORS[diff] || DIFF_COLORS.medium;

  const handleSubmit = () => {
    setSubmitted(true);
    onSubmit({ problemId: problem.id, userSolution, isCorrect, timeSpent: Math.floor((Date.now() - startTime()) / 1000) });
  };

  const goNext = () => { if (onNext) { onNext(); } };
  const goPrev = () => { if (onPrevious) { onPrevious(); } };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 20, width: '100%', maxWidth: 620,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '0.9rem 1.1rem', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0,
        }}>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, padding: '2px 9px', borderRadius: 6,
            background: diffStyle.bg, color: diffStyle.color, textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            {problem.difficulty || 'Medium'}
          </span>
          {totalProblems > 1 && (
            <span style={{ fontSize: '0.8rem', color: '#888', fontWeight: 600 }}>
              {currentIndex + 1} / {totalProblems}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>

          {/* Problem text */}
          <div style={{
            background: '#f5f3ff', borderLeft: '3px solid #5B35A0',
            borderRadius: 10, padding: '0.9rem 1rem',
            fontSize: '0.92rem', lineHeight: 1.7, color: '#1a1a2e',
          }}>
            <Md>{problem.problem_text}</Md>
          </div>

          {/* Hint */}
          {problem.short_hint && (
            <div>
              <button
                onClick={() => setShowHint(h => !h)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'none', border: '1.5px solid #fbbf24', borderRadius: 8,
                  padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600,
                  color: '#d97706', cursor: 'pointer',
                }}
              >
                <Lightbulb size={14} /> {showHint ? (translations.hideHint || 'Hide hint') : (translations.showHint || 'Show hint')}
              </button>
              {showHint && (
                <div style={{ marginTop: '0.5rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.87rem', color: '#78350f', lineHeight: 1.6 }}>
                  <Md>{problem.short_hint}</Md>
                </div>
              )}
            </div>
          )}

          {/* Answer textarea */}
          <textarea
            value={userSolution}
            onChange={e => setUserSolution(e.target.value)}
            placeholder={translations.writeSolution || 'Write your solution here…'}
            disabled={submitted}
            rows={6}
            style={{
              width: '100%', borderRadius: 10, border: '1.5px solid #e0d6f5',
              padding: '0.75rem 0.9rem', fontSize: '0.9rem', fontFamily: 'inherit',
              lineHeight: 1.6, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              background: submitted ? '#f9fafb' : 'white', color: '#1a1a2e',
            }}
          />

          {/* Self-eval */}
          {!submitted && userSolution.trim() && (
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                onClick={() => setIsCorrect(true)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '0.6rem', border: `2px solid ${isCorrect === true ? '#10b981' : '#d1fae5'}`,
                  borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                  background: isCorrect === true ? '#10b981' : 'white',
                  color: isCorrect === true ? 'white' : '#059669',
                }}
              >
                <CheckCircle size={16} /> {translations.correct || 'Correct'}
              </button>
              <button
                onClick={() => setIsCorrect(false)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '0.6rem', border: `2px solid ${isCorrect === false ? '#ef4444' : '#fee2e2'}`,
                  borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                  background: isCorrect === false ? '#ef4444' : 'white',
                  color: isCorrect === false ? 'white' : '#dc2626',
                }}
              >
                <XCircle size={16} /> {translations.incorrect || 'Wrong'}
              </button>
            </div>
          )}

          {/* Show answer */}
          {problem.model_answer && (
            <div>
              <button
                onClick={() => setShowAnswer(a => !a)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'none', border: '1.5px solid #10b981', borderRadius: 8,
                  padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600,
                  color: '#059669', cursor: 'pointer',
                }}
              >
                <Eye size={14} /> {showAnswer ? (translations.hideAnswer || 'Hide answer') : (translations.showAnswer || 'Show answer')}
              </button>
              {showAnswer && (
                <div style={{ marginTop: '0.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.87rem', color: '#065f46', lineHeight: 1.7 }}>
                  <Md>{problem.model_answer}</Md>
                </div>
              )}
            </div>
          )}

          {/* Complete solution */}
          {problem.complete_solution && showAnswer && (
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.87rem', color: '#5b21b6', lineHeight: 1.7 }}>
              <Md>{problem.complete_solution}</Md>
            </div>
          )}

          {/* Submission feedback */}
          {submitted && (
            <div style={{
              padding: '0.75rem 1rem', borderRadius: 10, textAlign: 'center',
              fontWeight: 700, fontSize: '0.9rem',
              background: isCorrect ? '#dcfce7' : '#fef3c7',
              color: isCorrect ? '#15803d' : '#92400e',
              border: `1.5px solid ${isCorrect ? '#86efac' : '#fde68a'}`,
            }}>
              {isCorrect ? `✅ ${translations.greatJob || 'Great job!'}` : `📝 ${translations.keepPracticing || 'Keep practicing!'}`}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '0.8rem 1.1rem', borderTop: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
        }}>
          {/* Prev */}
          <button
            onClick={goPrev}
            disabled={!hasPrevious}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: '1.5px solid #e0d6f5', borderRadius: 8,
              padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600,
              color: hasPrevious ? '#5B35A0' : '#ccc', cursor: hasPrevious ? 'pointer' : 'default',
            }}
          >
            <ChevronLeft size={15} /> {translations.prev || 'Prev'}
          </button>

          <div style={{ flex: 1 }} />

          {/* Submit */}
          {!submitted && (
            <button
              onClick={handleSubmit}
              disabled={!userSolution.trim() || isCorrect === null}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'linear-gradient(135deg,#5B35A0,#667eea)', border: 'none', borderRadius: 8,
                padding: '7px 18px', fontSize: '0.85rem', fontWeight: 700, color: 'white',
                cursor: (!userSolution.trim() || isCorrect === null) ? 'default' : 'pointer',
                opacity: (!userSolution.trim() || isCorrect === null) ? 0.5 : 1,
              }}
            >
              <Send size={14} /> {translations.submit || 'Submit'}
            </button>
          )}

          {/* Next */}
          <button
            onClick={hasNext ? goNext : onClose}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: hasNext ? '#5B35A0' : '#f3f4f6',
              border: 'none', borderRadius: 8,
              padding: '7px 14px', fontSize: '0.82rem', fontWeight: 700,
              color: hasNext ? 'white' : '#6b7280', cursor: 'pointer',
            }}
          >
            {hasNext ? (
              <>{translations.next || 'Next'} <ChevronRight size={15} /></>
            ) : (
              translations.done || 'Done'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeProblemModal;
